import { useState, useRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Button,
    Grid,
    IconButton,
    Hidden,
    Link,
    Tooltip,
    Typography
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DataGrid } from '@material-ui/data-grid';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import { makeStyles } from '@material-ui/core/styles';
import { Auth } from 'aws-amplify';
import { host, enableBilling, BillingCurrency, BillingPlans } from "../../../data";
import ModalPrompt from "../../OrgManage/Components/ModalPrompt";
import ActivateDevice from "./ActivateDevice.js";
import ActivateDevice2 from "./ActivateDevice2.js";
import { billing } from '../../../services/billing';
import StatusList from './StatusList.js';
import ChangeDeviceStatus from './ChangeDeviceStatus.js';
import ChangeDeviceStatus2 from './ChangeDeviceStatus2.js';
import { CSVLink } from "react-csv";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    indented: {
        marginLeft: theme.spacing(10),
    },
    grow: {
        flexGrow: 1,
    },
    content: {
        padding: theme.spacing(2),
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120
    },
    formControlLabel: {
        marginLeft: theme.spacing(2),
    },
    rootpaper: {
        width: 1000,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        overflowX: "auto"
    },
    table: {
        width: '100%',
        margin: '0 auto',
        maxWidth: '1400px',
        height: '60vh',
    },
    activate_device: {
        margin: '0 auto',
        maxWidth: '1200px',
        width: '100%',
    },
    link: {
        color: theme.palette.info.dark,
    },
    syncButton: {
        marginRight: theme.spacing(2),
    },
    buttonProgress: {
        marginRight: 64,
    },
}));

const ManageDevices = ({ orgInfo, devices, setDevices, fetchDevices=()=>{} }) => {
    const { t } = useTranslation();
    const baseurl = '/orgs/' + orgInfo.orgName;
    const classes = useStyles();
    const [csvReportData, setCSVReportData] = useState([]);
    const [csvFileName, setCSVFileName] = useState(orgInfo.orgName + '_devices.csv');
    const csvLink = useRef();
    const [targetUID, setTargetUID] = useState();
    const [targetNewStatus, setTargetNewStatus] = useState();
    const [openStatusChange, setOpenStatusChange] = useState(false);
    const [openDeviceSyncDialogue, setOpenDeviceSyncDialogue] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState();
    const [billingData, setBillingData] = useState({
        billing_info: {},
        email: '',
        subscriptions: [],
        invoices: [],
        cc_emails: [],
    });
    const [orgBilling, setOrgBilling] = useState({
        billing_curr: BillingCurrency[0],
        billing_plan: BillingPlans[0].plan_code
    });
    const csvHeaders = [
        { label: t("device"), key: 'uid' },
        { label: t("branch"), key: 'branchName' },
        { label: t("site"), key: 'siteName' },
        { label: t("tank"), key: 'tankName' },
        { label: t("status"), key: 'status' },
        { label: t("fluid"), key: 'fluidName' },
        { label: t("type"), key: 'type' },
    ];
    const deviceColumns = [
        {
            headerName: t("device"),
            field: 'uid',
            width: 160,
            disableClickEventBubbling: true
        },
        {
            headerName: t("branch"),
            field: 'branchName',
            renderCell: renderBranchLink,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("site"),
            field: 'siteName',
            renderCell: renderSiteLink,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("tank"),
            field: 'tankName',
            renderCell: renderLink,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("fluid"),
            field: 'fluidName',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("type"),
            field: 'type',
            renderCell: renderType,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("status"),
            field: 'status',
            renderCell: renderStatus,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("lastReading"),
            field: 'ServerTime_UTC',
            width: 190,
            type: 'dateTime',
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: '',
            field: 'Action',
            renderCell: renderAction,
            sortable: false,
            disableClickEventBubbling: true
        },
    ];

    const deviceColumnsMobile = [
        {
            headerName: t("device"),
            field: 'uid',
            width: 160,
            disableClickEventBubbling: true
        },
        {
            headerName: t("type"),
            field: 'type',
            width: 130,
            renderCell: renderType,
            disableClickEventBubbling: true
        },
        {
            headerName: t("status"),
            field: 'status',
            width: 130,
            renderCell: renderStatus,
            disableClickEventBubbling: true
        },
        {
            headerName: '',
            field: 'Action',
            width: 130,
            renderCell: renderAction,
            sortable: false,
            disableClickEventBubbling: true
        },
    ];


    const handleOpenDeleteDevice = (item) => {
        setDeleteTarget(item.uid);
        setOpenDelete(true);
    };

    const handleCloseDeleteDevice = () => {
        setDeleteTarget("");
        setOpenDelete(false);
    };

    const handleDeleteRow = () => {
        var delete_uid = deleteTarget;

        setOpenDelete(false);

        Auth.currentAuthenticatedUser()
            .then(data => {
                var body = JSON.stringify({ 'uid': delete_uid });
                var requestOptions = {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/ftdevices/', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        if (data.error === "OK") {
                            setDevices(data.deviceList);
                        }
                        else {
                            if (data.error === "error" && data.errorList.length > 0) {
                                alert(data.errorList[0].error);
                            } else {
                                alert(data.error)
                            }
                        }
                    }).catch((error) => {
                        console.log(error)
                    })
            });
    };

    function renderType(params) {
        return (
            <p>
                {params.value === "WiFi" ? t("wifi") : params.value === "PulseHUB" ? "Pulse Pro" : t(params.value)}
            </p>
        );
    };

    function renderLink(params) {
        if (params.row.tankName === undefined) {
            return;
        }

        return (
            <Tooltip title={params.value}>
                <Link className={classes.link}
                    component={RouterLink}
                    to={baseurl.concat('/branches/').concat(params.row.branchName).concat('/sites/').concat(params.row.siteName).concat('/tanks/').concat(params.row.tankName).concat('/edit/')}>
                    {params.value}
                </Link>
            </Tooltip>
        );
    };

    function renderSiteLink(params) {
        if (params.row.siteName === undefined) {
            return;
        }

        return (
            <Tooltip title={params.value}>
                <Link className={classes.link}
                    component={RouterLink}
                    to={baseurl.concat('/branches/').concat(params.row.branchName).concat('/sites/').concat(params.row.siteName)}>
                    {params.value}
                </Link>
            </Tooltip>
        );
    };

    function renderBranchLink(params) {
        if (params.row.branchName === undefined) {
            return;
        }

        return (
            <Tooltip title={params.value}>
                <Link className={classes.link}
                    component={RouterLink}
                    to={baseurl.concat('/branches/').concat(params.row.branchName)}>
                    {params.value}
                </Link>
            </Tooltip>
        );
    };

    function renderAction(params) {
        var ldisabled = params.row.status === 1 ? true : params.row.status === 3 ? true : false;

        return (
            <IconButton disabled={enableBilling && ldisabled} edge="end" aria-label="delete" onClick={() => handleOpenDeleteDevice(params.row)}>
                <DeleteIcon />
            </IconButton>
        );
    };

    function renderStatus(params) {
        var lstatus = t("disabled");
        var lfamily = params.row.type;
        var ldisabled = false;
        var l_uid = params.row.uid;

        switch (params.row.status) {
            case 0:
                lstatus = t("disabled");
                break;
            case 1:
                lstatus = t("active");
                break;
            case 2:
                lstatus = t("active");
                ldisabled = true;
                break;
            case 3:
                lstatus = t("inProcess");
                break;
            default:
                break;
        }

        if (false && (lfamily === "pressure" || lfamily === 'cellular') && !(l_uid.includes("Test")) && enableBilling) {
            return (
                <StatusList uid={l_uid} status_in={lstatus} disabled={ldisabled} setNewStatus={StatusChange} />
            );
        }
        else {
            return (<Typography variant="body2"> {lstatus} </Typography>);
        }
    };
    function StatusChange(uid, newStatus) {
        if (enableBilling) {
            // Update subscription
            setTargetUID(uid);
            setTargetNewStatus(newStatus);
            setOpenStatusChange(true);
        }
        else {
            // Update device status
        }
    };

    const handleCloseStatusChange = () => {
        setTargetUID();
        setTargetNewStatus();
        setOpenStatusChange(false);
    }

    const handleSyncDeviceStatus = () => {
        setOpenDeviceSyncDialogue(false);
        setIsSyncing(true);

        Auth.currentAuthenticatedUser()
            .then(data => {
                var body = JSON.stringify({ 'devices': [], 'hasBillingInfo': 0, 'sync': 1 });
                var requestOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    },
                    body: body
                };

                var url = host + '/orgs/' + orgInfo.orgId + '/ftdevices/';

                if (enableBilling) {
                    url = host + '/orgs/' + orgInfo.orgId + '/devices/';
                }

                fetch(url, requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        setIsSyncing(false);
                        if (data.error === "OK") {
                            setDevices(data.deviceList);
                        } else {
                            if (data.error === "error" && data.errorList.length > 0) {
                                alert(data.errorList[0].error);
                            }
                            else {
                                alert(data.error)
                            }
                        }
                    }).catch((error) => {
                        console.log(error)
                    })
            });
    };

    useEffect(() => {
        (async () => {
            try {
                const { status, data } = await billing.getBillingData(
                    orgInfo.orgId
                );

                const { error, billing_account, org_billing } = data;

                if (status === 404) {
                    // console.log(status);
                }

                if (error !== 'OK' && status !== 200) {
                    throw new Error(error);
                }

                setBillingData(billing_account);

                if (org_billing.billing_curr == null) {
                    org_billing.billing_curr = BillingCurrency[0];
                }
                if (org_billing.billing_plan == null) {
                    org_billing.billing_plan = BillingPlans[0].plan_code;
                }
                setOrgBilling(org_billing);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    function createCSVData(data_in) {
        var i;
        var k;
        var csvData = [[t('devices'), orgInfo.orgName]];
        let d = new Date;

        csvData.push([t('dateCreated'), convertDatetoCSVFormat(d)]);
        csvData.push(['']);

        var tempRow = [];

        for (i = 0; i < csvHeaders.length; i++) {
            tempRow.push(csvHeaders[i].label);
        }

        csvData.push(tempRow);

        if (data_in.length > 0) {
            for (k = 0; k < data_in.length; k++) {
                tempRow = []
                for (i = 0; i < csvHeaders.length; i++) {
                    if (data_in[k][csvHeaders[i].key] === null || data_in[k][csvHeaders[i].key] === undefined) {
                        tempRow.push("");
                    } else {
                        if (csvHeaders[i].key === "status") {
                            if (data_in[k][csvHeaders[i].key] == 0) {
                                tempRow.push(t("disabled"));
                            } else {
                                tempRow.push(t("active"));
                            }
                        }
                        else if(csvHeaders[i].key === "type")
                            if (data_in[k][csvHeaders[i].key] === "pulseHUB"){
                                tempRow.push("Pulse Pro");
                            }
                            else{
                                tempRow.push(t(data_in[k][csvHeaders[i].key]));
                            }
                        else {
                            tempRow.push(data_in[k][csvHeaders[i].key]);
                        }
                    }
                }
                csvData.push(tempRow);
            }
        }

        setCSVReportData(csvData);
    };

    function convertDatetoCSVFormat(inputTimeStamp) {
        //Excel based on 1990.  Java based on 1970.
        let ret = inputTimeStamp;

        if (!isNaN(inputTimeStamp.getTime())) {
            ret = inputTimeStamp.toISOString().slice(0, 19).replace('T', ' ').replace(/-/g, '/');
        }

        return ret;
    }

    const downloadCSVReport = (event) => {
        console.log("Downloading CSV for branch data...");
        csvLink.current.link.click();
    };

    useEffect(() => {
        createCSVData(devices);
    }, [devices]);

    return (
        <Grid container>
            <CSVLink
                data={csvReportData}
                filename={csvFileName}
                className='hidden'
                ref={csvLink}
                target='_blank' />
            {openStatusChange && <ChangeDeviceStatus orgInfo={orgInfo} orgBilling={orgBilling} billingData={billingData} setBillingData={setBillingData} uid={targetUID} newStatus={targetNewStatus} open={openStatusChange} handleClose={handleCloseStatusChange} setDevices={setDevices} />}
            <Grid item container xs="12">
                <div className={classes.activate_device}>
                    <Grid container item alignItems="center">
                        <Grid item>
                            {false && <ActivateDevice orgInfo={orgInfo} setDevices={setDevices} billingData={billingData} setBillingData={setBillingData} orgBilling={orgBilling} />}
                            {true && <ActivateDevice2 orgInfo={orgInfo} setDevices={setDevices} billingData={billingData} setBillingData={setBillingData} orgBilling={orgBilling} />}
                        </Grid>
                        <div className={classes.grow} />
                        <Grid item>
                            <Tooltip title={t('refresh')}>
                                <IconButton
                                    aria-haspopup='true'
                                    onClick={fetchDevices}
                                    color='inherit'
                                    className={classes.iconButtons}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        {false && enableBilling && <Grid item className={classes.syncButton}>
                            {isSyncing ? <CircularProgress size={24} className={classes.buttonProgress} />
                                : <Button variant="outlined" onClick={handleSyncDeviceStatus} color="primary">
                                    {t('syncStatus_caps')}
                                </Button>}
                        </Grid>}
                        {enableBilling && <Grid item>
                            <ChangeDeviceStatus2 orgInfo={orgInfo} orgBilling={orgBilling} billingData={billingData} setBillingData={setBillingData} devices={devices} setDevices={setDevices} />
                        </Grid>}
                        <Grid item>
                            <Tooltip title={t("Download")}>
                                <IconButton aria-label="download-table" variant="contained" onClick={() => downloadCSVReport()}>
                                    <GetAppIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </div>
            </Grid>
            <Grid item xs="12">
                {openDelete && <ModalPrompt title={t("delete1") + ' ' + deleteTarget + '?'}
                    content={t("areYouSure")}
                    action="Delete"
                    handleAction={handleDeleteRow}
                    handleCancel={handleCloseDeleteDevice}
                    openModal={openDelete} />}
                <Hidden smDown>
                    <div className={classes.table}>
                        <DataGrid hideFooterSelectedRowCount
                            rowsPerPageOptions={[25, 50, 100]}
                            pagination
                            pageSize={100}
                            rows={devices}
                            columns={deviceColumns} />
                    </div>
                </Hidden>
                <Hidden mdUp>
                    <div className={classes.table}>
                        <DataGrid hideFooterSelectedRowCount
                            rowsPerPageOptions={[25, 50, 100]}
                            pagination
                            pageSize={100}
                            rows={devices}
                            columns={deviceColumnsMobile} />
                    </div>
                </Hidden>
            </Grid>
        </Grid>
    );
};

export default ManageDevices;